/**
 * EventCard component for displaying an event in lists or grids
 * Unified component with all variants and utilities
 * Simple drawer implementation following Shadcn/UI best practices
 */

'use client';

import { ThemeBadges } from '@uicwebmaster/taxonomy-themes/components';
import { EyeIcon, MapPin, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { EventDateBadgeOverlay, EventDateTime, EventImage, EventTypes } from '../components/event';
import { buttonVariants } from '../components/ui/button';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from '../components/ui/drawer';
import { HighlightedText } from '../components/ui/highlighted-text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { useEventsComponents } from '../config';
import type { Event } from '../types';
import { darkenHexColorWithOpacity as defaultDarkenHexColor } from '../utils/color';
import { extractEventTypeData } from '../utils/event-type';
import { EventDetail } from '../views/event-detail';
import { AddToCalendar } from './add-to-calendar';

// ============================================================================
// Simple Event Drawer Context (minimal, just for opening drawer)
// ============================================================================

interface EventDrawerContextValue {
  openDrawer: (event: Event) => void;
}

const EventDrawerContext = createContext<EventDrawerContextValue | null>(null);

/**
 * Hook to access event drawer context
 * Returns null if not within EventDrawerProvider
 */
export function useEventDrawer() {
  const context = useContext(EventDrawerContext);
  return context; // Returns null if not in provider
}

/**
 * Props for EventDrawerProvider
 */
export interface EventDrawerProviderProps {
  children: ReactNode;
  editButton?: (event: Event) => ReactNode;
}

/**
 * Simple Event Drawer Provider
 * Following Shadcn/UI best practices - minimal Context just for openDrawer
 * CSS handles responsive behavior (no useMediaQuery needed)
 */
export function EventDrawerProvider({ children, editButton }: EventDrawerProviderProps) {
  const [openEvent, setOpenEvent] = useState<Event | null>(null);

  const openDrawer = useCallback((event: Event) => {
    setOpenEvent(event);
  }, []);

  const contextValue = useMemo(() => ({ openDrawer }), [openDrawer]);

  return (
    <EventDrawerContext.Provider value={contextValue}>
      {children}

      {/* Drawer controlled by state, responsive via CSS */}
      <Drawer open={openEvent !== null} onOpenChange={open => !open && setOpenEvent(null)}>
        {openEvent && (
          <DrawerContent>
            <DrawerHeader className="border-b">
              <div className="flex items-center justify-between">
                <DrawerTitle className="sr-only">{openEvent.title}</DrawerTitle>
                <DrawerClose asChild>
                  <button
                    className="rounded-full p-2 hover:bg-muted transition-colors ml-auto"
                    aria-label="Close drawer"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </DrawerClose>
              </div>
            </DrawerHeader>

            <div className="h-full overflow-y-auto p-0">
              <EventDetail event={openEvent} editButton={editButton?.(openEvent)} />
            </div>
          </DrawerContent>
        )}
      </Drawer>
    </EventDrawerContext.Provider>
  );
}

// ============================================================================
// Event Card Types
// ============================================================================

export type EventCardLayout = 'default' | 'grid' | 'list' | 'compact' | 'featured';

export interface EventCardData {
  startTimestamp: number;
  endTimestamp: number;
  startDate: Date | null;
  endDate: Date | null;
  isSameDay: boolean;
  isPast: boolean;
  excerpt: string | null;
  firstEventType: { id: string; name: string } | null;
  eventTypeColor: string | null | undefined;
}

export interface EventCardProps {
  event: Event;
  /** Base URL for event links (e.g., '/events') */
  baseUrl?: string;
  /** Layout variant for the card */
  variant?: EventCardLayout;
  /** Show past events differently */
  showPastIndicator?: boolean;
  /** Whether the entire card should be clickable (default: true) */
  clickable?: boolean;
  /** Whether to show the event type background color (default: true) */
  showEventTypeBackground?: boolean;
  /** Optional edit button component (e.g., DrupalEntityEditButton) */
  editButton?: React.ReactNode;
  // Legacy props for compatibility (not used but kept for interface compatibility)
  showImage?: boolean;
  showDate?: boolean;
  showLocation?: boolean;
  showEventType?: boolean;
  showExcerpt?: boolean;
}

// ============================================================================
// Main EventCard Component
// ============================================================================

export function EventCard({
  event,
  baseUrl = '/events',
  variant = 'default',
  showPastIndicator = true,
  clickable = true,
  showEventTypeBackground = true,
  editButton,
}: EventCardProps) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const Image = config.ImageComponent;
  const darkenHexColorWithOpacity = config.darkenHexColorWithOpacity || defaultDarkenHexColor;

  const data = extractEventCardData(event);
  const eventUrl = `${baseUrl}${event.path}`;

  // For default variant, choose between clickable and non-clickable based on prop
  if (!clickable) {
    return (
      <EventCardDefaultNonClickable
        event={event}
        eventUrl={eventUrl}
        data={data}
        showPastIndicator={showPastIndicator}
        showEventTypeBackground={showEventTypeBackground}
        editButton={editButton}
      />
    );
  }

  return (
    <EventCardDefault
      event={event}
      eventUrl={eventUrl}
      data={data}
      showPastIndicator={showPastIndicator}
      showEventTypeBackground={showEventTypeBackground}
      editButton={editButton}
    />
  );
}

// ============================================================================
// Default Clickable Card
// ============================================================================

function EventCardDefault({
  event,
  eventUrl,
  data,
  showPastIndicator = true,
  showEventTypeBackground = true,
  editButton,
}: {
  event: Event;
  eventUrl: string;
  data: EventCardData;
  showPastIndicator?: boolean;
  showEventTypeBackground?: boolean;
  editButton?: React.ReactNode;
}) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const Image = config.ImageComponent;
  const darkenHexColorWithOpacity = config.darkenHexColorWithOpacity || defaultDarkenHexColor;
  const { eventTypeColor } = data;
  const drawerContext = useEventDrawer();

  const cardClassName = `group flex h-full w-full max-w-full flex-col overflow-hidden rounded shadow-sm transition-all hover:shadow-lg ${
    showEventTypeBackground && eventTypeColor ? '' : 'bg-card'
  }`;
  const cardStyle = {
    backgroundColor:
      showEventTypeBackground && eventTypeColor
        ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.1)
        : undefined,
  };

  const content = (
    <EventCardDefaultContent
      event={event}
      eventUrl={eventUrl}
      data={data}
      showPastIndicator={showPastIndicator}
      Image={Image}
      editButton={editButton}
    />
  );

  // If drawer context available, use button with onClick
  if (drawerContext) {
    return (
      <button
        onClick={() => drawerContext.openDrawer(event)}
        className={`${cardClassName} cursor-pointer text-left`}
        style={cardStyle}
      >
        {content}
      </button>
    );
  }

  // Otherwise render as link
  return (
    <Link href={eventUrl} className={cardClassName} style={cardStyle}>
      {content}
    </Link>
  );
}

// ============================================================================
// Default Non-Clickable Card (with Details button)
// ============================================================================

function EventCardDefaultNonClickable({
  event,
  eventUrl,
  data,
  showPastIndicator = true,
  showEventTypeBackground = true,
  editButton,
}: {
  event: Event;
  eventUrl: string;
  data: EventCardData;
  showPastIndicator?: boolean;
  showEventTypeBackground?: boolean;
  editButton?: React.ReactNode;
}) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const Image = config.ImageComponent;
  const searchQuery = config.searchQuery || '';
  const {
    startTimestamp,
    endTimestamp,
    startDate,
    endDate,
    isSameDay,
    isPast,
    excerpt,
    firstEventType,
    eventTypeColor,
  } = data;
  const drawerContext = useEventDrawer();
  const imageMeta = event.image ?? null;
  const imageUrl = imageMeta?.url ?? null;
  const imageSizes =
    '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw';

  return (
    <div
      className={`flex h-full w-full max-w-full flex-col overflow-hidden rounded shadow-sm transition-all hover:shadow-lg bg-slate-500/5`}
    >
      {/* Image or date badge */}
      <div className="relative aspect-16/6 lg:aspect-16/8 overflow-hidden">
        <EventImage
          imageUrl={imageUrl}
          alt={imageMeta?.alt || event.title}
          width={imageMeta?.width || 800}
          height={imageMeta?.height || 450}
          eventTypeColor={eventTypeColor}
          ImageComponent={Image}
          sizes={imageSizes}
        />
        {/* Date badge overlay */}
        {startDate && (
          <EventDateBadgeOverlay date={startDate} isPast={isPast && showPastIndicator} />
        )}

        {/* Top-right badges: event type + edit button, stacked to avoid overlap */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col items-end gap-1.5 sm:gap-2">
          {firstEventType && (
            <EventTypes
              eventTypes={[
                {
                  id: firstEventType.id,
                  name: firstEventType.name,
                  eventTypeColor: eventTypeColor,
                },
              ]}
              variant="card"
            />
          )}
          {editButton}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-3 sm:p-4 min-w-0">
        {/* Title */}
        <h3 className="line-clamp-2 mb-2 text-base sm:text-lg font-semibold leading-tight break-words min-w-0">
          <HighlightedText text={event.title} query={searchQuery} />
        </h3>
        <div className="space-y-2">
          {/* Date and time */}
          {startTimestamp > 0 && startDate && (
            <EventDateTime
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              startDate={startDate}
              endDate={endDate}
              isSameDay={isSameDay}
              size="sm"
            />
          )}

          {/* Location */}
          {event.eventLocation && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1 min-w-0 break-words">
                <HighlightedText text={event.eventLocation} query={searchQuery} />
              </span>
            </div>
          )}

          {/* Themes */}
          {event.theme && event.theme.length > 0 && (
            <ThemeBadges themes={event.theme} maxVisible={2} showIcon={true} />
          )}
        </div>
      </div>
      {/* Actions: Details button + Add to Calendar */}
      <div className="flex p-3 flex-row justify-between items-center gap-2">
        {startDate && (
          <AddToCalendar
            event={{
              title: event.title,
              startDate: startDate,
              endDate: endDate,
              location: event.eventLocation || null,
              description: excerpt || null,
              url: eventUrl || null,
            }}
            variant="ghost"
          />
        )}

        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            {drawerContext ? (
              <button
                onClick={() => drawerContext.openDrawer(event)}
                className={buttonVariants({
                  variant: 'outline',
                  size: 'sm',
                  className: 'shrink-0 border-none',
                })}
              >
                <span className="sr-only">Details</span>
                <EyeIcon className="h-4 w-4" />
              </button>
            ) : (
              <Link
                href={eventUrl}
                className={buttonVariants({
                  variant: 'outline',
                  size: 'sm',
                  className: 'shrink-0 border-none',
                })}
              >
                <span className="sr-only">Details</span>
                <EyeIcon className="h-4 w-4" />
              </Link>
            )}
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            <div className="font-semibold">Details</div>
            <div className="font-extralight">View event details</div>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ============================================================================
// Card Content Components
// ============================================================================

function EventCardDefaultContent({
  event,
  eventUrl,
  data,
  showPastIndicator,
  Image,
  editButton,
}: {
  event: Event;
  eventUrl: string;
  data: EventCardData;
  showPastIndicator: boolean;
  Image: any;
  editButton?: React.ReactNode;
}) {
  const config = useEventsComponents();
  const searchQuery = config.searchQuery || '';
  const {
    startTimestamp,
    endTimestamp,
    startDate,
    endDate,
    isSameDay,
    isPast,
    excerpt,
    firstEventType,
    eventTypeColor,
  } = data;
  const imageMeta = event.image ?? null;
  const imageUrl = imageMeta?.url ?? null;
  const imageSizes =
    '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw';

  return (
    <>
      {/* Image or date badge */}
      <div className="relative aspect-16/6 lg:aspect-16/8 overflow-hidden">
        <EventImage
          imageUrl={imageUrl}
          alt={imageMeta?.alt || event.title}
          width={imageMeta?.width || 800}
          height={imageMeta?.height || 450}
          eventTypeColor={eventTypeColor}
          ImageComponent={Image}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          sizes={imageSizes}
        />
        {/* Date badge overlay */}
        {startDate && (
          <EventDateBadgeOverlay date={startDate} isPast={isPast && showPastIndicator} />
        )}

        {/* Top-right badges: event type + edit button, stacked to avoid overlap */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col items-end gap-1.5 sm:gap-2">
          {firstEventType && (
            <EventTypes
              eventTypes={[
                {
                  id: firstEventType.id,
                  name: firstEventType.name,
                  eventTypeColor: eventTypeColor,
                },
              ]}
              variant="card"
            />
          )}
          {editButton}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-3 sm:p-4 min-w-0">
        {/* Title */}
        <h3 className="line-clamp-2 mb-2 text-base sm:text-lg font-semibold leading-tight break-words min-w-0">
          <HighlightedText text={event.title} query={searchQuery} />
        </h3>
        <div className="space-y-2">
          {/* Date and time */}
          {startTimestamp > 0 && startDate && (
            <EventDateTime
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              startDate={startDate}
              endDate={endDate}
              isSameDay={isSameDay}
              size="sm"
            />
          )}

          {/* Location */}
          {event.eventLocation && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1 min-w-0 break-words">
                <HighlightedText text={event.eventLocation} query={searchQuery} />
              </span>
            </div>
          )}

          {/* Themes */}
          {event.theme && event.theme.length > 0 && (
            <ThemeBadges themes={event.theme} maxVisible={2} showIcon={true} />
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

function extractEventCardData(event: Event): EventCardData {
  const startTimestamp = event.eventDateTimeRange?.start?.timestamp || 0;
  const endTimestamp = event.eventDateTimeRange?.end?.timestamp || 0;
  const startDate = startTimestamp > 0 ? new Date(startTimestamp * 1000) : null;
  const endDate = endTimestamp > 0 ? new Date(endTimestamp * 1000) : null;
  const isSameDay =
    startDate && endDate ? startDate.toDateString() === endDate.toDateString() : false;
  const isPast = startDate ? startDate < new Date() : false;
  const excerpt = event.body?.summary || event.body?.processed || null;
  const { firstEventType, eventTypeColor } = extractEventTypeData(event);

  return {
    startTimestamp,
    endTimestamp,
    startDate,
    endDate,
    isSameDay,
    isPast,
    excerpt,
    firstEventType,
    eventTypeColor,
  };
}

// ============================================================================
// Date Formatting Utilities (for backwards compatibility)
// ============================================================================

export function formatEventDate(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatEventTime(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
